/**
 * Turning raw samples into an honest timeline.
 *
 * This is the core of the project. Everything else — probes, charts, storage —
 * is commodity. The one thing off-the-shelf uptime monitors get wrong is that
 * they treat "no data" as "down", which on a box that occasionally reboots turns
 * an uptime figure into fiction. Every function here exists to keep the two apart.
 *
 * Pure functions only: no clock reads, no I/O. The caller supplies `now`.
 */
/** Nominal spacing between probe cycles. */
export const PROBE_INTERVAL_MS = 60_000;
/**
 * How long a sample is considered to "cover" before we call it a gap. Two and a
 * half intervals tolerates one lost cycle and ordinary scheduling jitter without
 * manufacturing a gap; a third consecutive miss is real and gets reported.
 */
export const GAP_TOLERANCE_MS = PROBE_INTERVAL_MS * 2.5;
/** Classify a single probe cycle. Order matters: a dead first hop outranks WAN. */
export function classifySample(s) {
    if (!s.localOk)
        return 'local_network';
    if (!s.wanOk)
        return 'isp_outage';
    return 'up';
}
/**
 * Build a contiguous, gap-free timeline over `[from, to)`.
 *
 * Guarantees, all asserted in the tests:
 *  - segments are sorted, non-overlapping, and tile the window exactly
 *  - any stretch not covered by a sample is `monitoring_gap`, never `up`/`down`
 *  - adjacent segments of the same classification are merged
 */
export function buildTimeline(samples, from, to) {
    if (to <= from)
        return [];
    // Defensive: callers pass Firestore query results, which we don't want to
    // trust for ordering or duplicates.
    const sorted = [...samples]
        .filter((s) => Number.isFinite(s.ts))
        .sort((a, b) => a.ts - b.ts)
        .filter((s, i, arr) => i === 0 || s.ts !== arr[i - 1].ts);
    const raw = [];
    let cursor = from;
    for (const s of sorted) {
        // A sample covers forward from its own timestamp.
        const covStart = Math.max(s.ts, from);
        const covEnd = Math.min(s.ts + PROBE_INTERVAL_MS, to);
        if (covEnd <= from || covStart >= to)
            continue; // wholly outside the window
        if (covStart > cursor) {
            raw.push({ startTs: cursor, endTs: covStart, classification: 'monitoring_gap', sampleCount: 0 });
        }
        // `covStart < cursor` means this sample overlaps the previous one's coverage
        // (cadence ran fast). Clamp rather than double-count.
        const segStart = Math.max(covStart, cursor);
        if (covEnd > segStart) {
            raw.push({ startTs: segStart, endTs: covEnd, classification: classifySample(s), sampleCount: 1 });
            cursor = covEnd;
        }
    }
    if (cursor < to) {
        raw.push({ startTs: cursor, endTs: to, classification: 'monitoring_gap', sampleCount: 0 });
    }
    return mergeAdjacent(raw);
}
function mergeAdjacent(segs) {
    const out = [];
    for (const seg of segs) {
        const prev = out[out.length - 1];
        if (prev && prev.classification === seg.classification && prev.endTs === seg.startTs) {
            prev.endTs = seg.endTs;
            prev.sampleCount += seg.sampleCount;
        }
        else {
            out.push({ ...seg });
        }
    }
    return out;
}
/**
 * Re-classify an outage that the outside watcher opened from a missing heartbeat,
 * now that the prober's buffered samples for that window have arrived.
 *
 * The watcher only knows "the house went quiet". These samples are what tell us
 * *why*, and in particular whether it was quiet because the link died or because
 * the monitoring box did.
 */
export function reconcileOutage(outage, samples, now) {
    const end = outage.endTs ?? now;
    const covering = samples
        .filter((s) => s.ts + PROBE_INTERVAL_MS > outage.startTs && s.ts < end)
        .sort((a, b) => a.ts - b.ts);
    if (covering.length === 0) {
        return {
            classification: 'monitoring_gap',
            reason: 'No prober samples exist for this window. The prober, its pod, k3s, WSL2 or the host was down — ' +
                'this is unmonitored time, not an internet outage, and is excluded from the uptime denominator.',
            sampleCount: 0,
        };
    }
    const localDown = covering.filter((s) => !s.localOk).length;
    const wanDown = covering.filter((s) => s.localOk && !s.wanOk).length;
    const healthy = covering.filter((s) => s.localOk && s.wanOk).length;
    // Whichever failure the prober actually observed most of the time wins. A
    // dead first hop is reported ahead of a dead WAN: if the house-side network is
    // down, everything beyond it is unreachable as a consequence, not as a cause.
    if (localDown > 0 && localDown >= wanDown) {
        return {
            classification: 'local_network',
            reason: `Prober was alive and reported its first hop unreachable on ${localDown}/${covering.length} cycles — a house-side fault, not the ISP.`,
            sampleCount: covering.length,
        };
    }
    if (wanDown > 0) {
        return {
            classification: 'isp_outage',
            reason: `Prober was alive with a healthy first hop but could not reach any WAN target on ${wanDown}/${covering.length} cycles — the link beyond the house was down.`,
            sampleCount: covering.length,
        };
    }
    return {
        classification: 'reporting_gap',
        reason: `Prober measured a healthy link across all ${healthy} cycles in this window, so the connection was up — ` +
            'its data simply never reached the collector in time. The failure was in the reporting path (collector, auth or upload), not the internet.',
        sampleCount: covering.length,
    };
}
const EMPTY_BY_CLASS = () => ({
    up: 0,
    isp_outage: 0,
    local_network: 0,
    monitoring_gap: 0,
    reporting_gap: 0,
});
/** `reporting_gap` counts as up: the prober verified the link was fine. */
const UP_CLASSES = ['up', 'reporting_gap'];
const DOWN_CLASSES = ['isp_outage', 'local_network'];
export function summarize(segments) {
    const msByClass = EMPTY_BY_CLASS();
    for (const s of segments) {
        msByClass[s.classification] += Math.max(0, s.endTs - s.startTs);
    }
    const upMs = UP_CLASSES.reduce((n, c) => n + msByClass[c], 0);
    const downMs = DOWN_CLASSES.reduce((n, c) => n + msByClass[c], 0);
    const unmonitoredMs = msByClass.monitoring_gap;
    const monitoredMs = upMs + downMs;
    const windowMs = monitoredMs + unmonitoredMs;
    return {
        windowMs,
        monitoredMs,
        upMs,
        downMs,
        unmonitoredMs,
        msByClass,
        uptimePct: monitoredMs > 0 ? (upMs / monitoredMs) * 100 : null,
        coveragePct: windowMs > 0 ? (monitoredMs / windowMs) * 100 : 0,
    };
}
/**
 * Extract user-visible outages from a timeline.
 *
 * Only genuine connectivity failures become outages. Monitoring gaps are
 * deliberately excluded — they belong in the coverage figure, not the outage log,
 * and folding them in is precisely the mistake this project exists to avoid.
 * `minDurationMs` suppresses single-cycle blips that are usually probe noise.
 */
export function extractOutages(segments, minDurationMs = PROBE_INTERVAL_MS) {
    return segments.filter((s) => DOWN_CLASSES.includes(s.classification) && s.endTs - s.startTs >= minDurationMs);
}
/** Median of a numeric list, ignoring null/NaN. Returns null when nothing is usable. */
export function median(values) {
    const nums = values.filter((v) => typeof v === 'number' && Number.isFinite(v)).sort((a, b) => a - b);
    if (nums.length === 0)
        return null;
    const mid = Math.floor(nums.length / 2);
    return nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}
/** Nearest-rank percentile (p in 0..100). */
export function percentile(values, p) {
    const nums = values.filter((v) => typeof v === 'number' && Number.isFinite(v)).sort((a, b) => a - b);
    if (nums.length === 0)
        return null;
    const rank = Math.ceil((p / 100) * nums.length);
    return nums[Math.min(Math.max(rank, 1), nums.length) - 1];
}
/**
 * Mean absolute difference between consecutive RTTs — how much latency wobbles.
 * This, not raw bandwidth, is what makes calls break up.
 */
export function jitter(rtts) {
    const nums = rtts.filter((v) => typeof v === 'number' && Number.isFinite(v));
    if (nums.length < 2)
        return null;
    let total = 0;
    for (let i = 1; i < nums.length; i++)
        total += Math.abs(nums[i] - nums[i - 1]);
    return total / (nums.length - 1);
}
//# sourceMappingURL=classify.js.map